import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import Konva from "konva";
import { EditorTool, EditorObject, CanvasSize, DisplaySize, generateId } from "../types/editorTypes";

interface UseCanvasDrawingProps {
  stageRef: React.RefObject<Konva.Stage>;
  containerRef: React.RefObject<HTMLDivElement>;
  transformerRef: React.RefObject<Konva.Transformer>;
  sourceImg: HTMLImageElement | null;
  // ツール設定
  currentTool: EditorTool;
  setCurrentTool: (tool: EditorTool) => void;
  // マーカー設定
  markerColor: string;
  markerStrokeWidth: number;
  // 四角形設定
  rectFillEnabled: boolean;
  rectFillColor: string;
  rectStrokeEnabled: boolean;
  rectStrokeColor: string;
  // テキスト設定
  textFontSize: number;
  textFontFamily: string;
  textFontStyle: 'normal' | 'bold' | 'italic';
  textAlign: 'left' | 'center' | 'right' | 'justify';
  verticalAlign: 'top' | 'middle' | 'bottom';
  textColor: string;
  textBgColor: string;
  // 帯削除コールバック
  onBandDelete?: () => void;
}

interface UseCanvasDrawingReturn {
  // State
  objects: EditorObject[];
  setObjects: React.Dispatch<React.SetStateAction<EditorObject[]>>;
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  isDrawing: boolean;
  currentPoints: number[];
  canvasSize: CanvasSize;
  displaySize: DisplaySize;
  cursorVisible: boolean;
  loadedImages: { [key: string]: HTMLImageElement };
  // テキスト編集
  editingTextId: string | null;
  setEditingTextId: (id: string | null) => void;
  editingTextValue: string;
  setEditingTextValue: (value: string) => void;
  editingTextPosition: { x: number; y: number; width: number; height: number } | null;
  setEditingTextPosition: (pos: { x: number; y: number; width: number; height: number } | null) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  // Handlers
  handleMouseDown: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  handleMouseMove: () => void;
  handleMouseUp: () => void;
  handleStageClick: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  handleObjectSelect: (id: string) => void;
  handleDragEnd: (id: string, e: Konva.KonvaEventObject<DragEvent>) => void;
  handleDeleteSelected: () => void;
  handleTextDoubleClick: (obj: EditorObject) => void;
  handleRectDoubleClick: (obj: EditorObject) => void;
  handleTextEditConfirm: (newHeight?: number) => void;
  updateTextProperty: (property: keyof EditorObject, value: any) => void;
  updateRectProperty: (property: keyof EditorObject, value: any) => void;
  handleAddImage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  refreshTransformer: () => void;
}

export const useCanvasDrawing = ({
  stageRef,
  containerRef,
  transformerRef,
  sourceImg,
  currentTool,
  setCurrentTool,
  markerColor,
  markerStrokeWidth,
  rectFillEnabled,
  rectFillColor,
  rectStrokeEnabled,
  rectStrokeColor,
  textFontSize,
  textFontFamily,
  textFontStyle,
  textAlign,
  verticalAlign,
  textColor,
  textBgColor,
  onBandDelete,
}: UseCanvasDrawingProps): UseCanvasDrawingReturn => {
  // State
  const [objects, setObjects] = useState<EditorObject[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState<number[]>([]);
  const [canvasSize, setCanvasSize] = useState<CanvasSize>({ width: 800, height: 600 });
  const [cursorVisible, setCursorVisible] = useState(true);
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: HTMLImageElement }>({});

  // テキスト編集
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [editingTextValue, setEditingTextValue] = useState('');
  const [editingTextPosition, setEditingTextPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // コンテナサイズを監視（ResizeObserverを使用）
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          const width = Math.max(rect.width, 400);
          const height = Math.max(rect.height, 300);
          setCanvasSize(prev => {
            if (prev.width === width && prev.height === height) {
              return prev;
            }
            return { width, height };
          });
        }
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      updateSize();
    });
    resizeObserver.observe(containerRef.current);
    updateSize();

    return () => {
      resizeObserver.disconnect();
    };
  }, [sourceImg, containerRef]);

  // Transformer更新
  useEffect(() => {
    if (!transformerRef.current || !stageRef.current) return;

    if (editingTextId) {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer()?.batchDraw();
      return;
    }

    const stage = stageRef.current;
    const transformer = transformerRef.current;

    // 一度Transformerを外す
    transformer.nodes([]);

    // 次のフレームで再アタッチ（Reactの再レンダリング完了後）
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!stageRef.current || !transformerRef.current) return;
        const selectedNode = stageRef.current.findOne(`#${selectedId}`);
        if (selectedNode) {
          transformerRef.current.nodes([selectedNode]);
        }
        transformerRef.current.getLayer()?.batchDraw();
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [selectedId, editingTextId, transformerRef, stageRef, objects]);

  // Transformerを手動で更新する関数
  const refreshTransformer = useCallback(() => {
    if (transformerRef.current && stageRef.current && selectedId) {
      const transformer = transformerRef.current;
      const stage = stageRef.current;
      const selectedNode = stage.findOne(`#${selectedId}`);
      if (selectedNode) {
        // 一度ノードを外す
        transformer.nodes([]);
        // すぐに再アタッチしてforceUpdateを呼ぶ
        transformer.nodes([selectedNode]);
        // Transformerに強制的にバウンディングボックスを再計算させる
        (transformer as any).forceUpdate();
        transformer.getLayer()?.batchDraw();
      }
    }
  }, [selectedId, transformerRef, stageRef]);

  // キーボードイベント（バックスペース/Deleteで削除）
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (editingTextId) return;
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        return;
      }

      if ((e.key === 'Backspace' || e.key === 'Delete') && selectedId) {
        e.preventDefault();
        if (selectedId === 'band-image') {
          // 帯画像が選択されている場合は帯削除コールバックを呼ぶ
          if (onBandDelete) {
            onBandDelete();
            setSelectedId(null);
          }
          return;
        }
        setObjects(prev => prev.filter(obj => obj.id !== selectedId));
        setSelectedId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, editingTextId, onBandDelete]);

  // カーソル点滅
  useEffect(() => {
    if (editingTextId) {
      const interval = setInterval(() => {
        setCursorVisible(prev => !prev);
      }, 530);
      return () => clearInterval(interval);
    } else {
      setCursorVisible(true);
    }
  }, [editingTextId]);

  // 画像表示サイズを計算
  const displaySize = useMemo(() => {
    if (sourceImg && canvasSize.width > 0 && canvasSize.height > 0) {
      const padding = 20; // キャンバス内のパディング
      const availableWidth = canvasSize.width - padding * 2;
      const availableHeight = canvasSize.height - padding * 2;

      const imgAspect = sourceImg.width / sourceImg.height;
      const canvasAspect = availableWidth / availableHeight;

      let width, height;
      if (imgAspect > canvasAspect) {
        // 画像が横長 - 幅に合わせる
        width = availableWidth;
        height = availableWidth / imgAspect;
      } else {
        // 画像が縦長 - 高さに合わせる
        height = availableHeight;
        width = availableHeight * imgAspect;
      }

      const offsetX = (canvasSize.width - width) / 2;
      const offsetY = (canvasSize.height - height) / 2;
      return { width, height, offsetX, offsetY };
    }
    return { width: 0, height: 0, offsetX: 0, offsetY: 0 };
  }, [sourceImg, canvasSize]);

  // テキストの書式を更新
  const updateTextProperty = useCallback((property: keyof EditorObject, value: any) => {
    if (!selectedId) return;
    setObjects(prev => prev.map(obj => {
      if (obj.id === selectedId && obj.type === 'text') {
        return { ...obj, [property]: value };
      }
      return obj;
    }));
  }, [selectedId]);

  // 四角形のプロパティを更新
  const updateRectProperty = useCallback((property: keyof EditorObject, value: any) => {
    if (!selectedId) return;
    setObjects(prev => prev.map(obj => {
      if (obj.id === selectedId && obj.type === 'rect') {
        return { ...obj, [property]: value };
      }
      return obj;
    }));
  }, [selectedId]);

  // テキスト編集確定
  const handleTextEditConfirm = useCallback((newHeight?: number) => {
    if (editingTextId) {
      const editingObj = objects.find(obj => obj.id === editingTextId);
      if (editingTextValue.trim()) {
        // テキストがある場合は更新（高さも更新）
        setObjects(prev => prev.map(obj => {
          if (obj.id === editingTextId) {
            return {
              ...obj,
              text: editingTextValue,
              // 新しい高さが元の高さより大きい場合のみ更新
              height: newHeight && newHeight > (obj.height || 0) ? newHeight : obj.height,
            };
          }
          return obj;
        }));
      } else if (editingObj?.type === 'text') {
        // テキストオブジェクトで空の場合のみ削除
        setObjects(prev => prev.filter(obj => obj.id !== editingTextId));
      } else {
        // 四角形の場合はテキストを空に更新
        setObjects(prev => prev.map(obj => {
          if (obj.id === editingTextId) {
            return { ...obj, text: '' };
          }
          return obj;
        }));
      }
    }
    setEditingTextId(null);
    setEditingTextValue('');
    setEditingTextPosition(null);
    // テキスト追加後はツール選択をOFFにする
    setCurrentTool('select');
  }, [editingTextId, editingTextValue, objects, setCurrentTool]);

  // ステージクリック処理
  const handleStageClick = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    const clickedOnEmpty = e.target === e.target.getStage() || e.target.name() === 'background';

    // 編集中のテキストがあれば確定
    if (editingTextId) {
      handleTextEditConfirm();
      return;
    }

    if (clickedOnEmpty) {
      // テキストツールの場合はドラッグで作成するので、クリックでは何もしない
      if (currentTool !== 'text') {
        setSelectedId(null);
      }
    }
  }, [editingTextId, currentTool, handleTextEditConfirm]);

  // テキストボックスをダブルクリックして編集開始
  const handleTextDoubleClick = useCallback((obj: EditorObject) => {
    setSelectedId(obj.id);
    setEditingTextId(obj.id);
    setEditingTextValue(obj.text || '');
    setEditingTextPosition({
      x: obj.x,
      y: obj.y,
      width: obj.width || 150,
      height: obj.height || 24,
    });
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const len = (obj.text || '').length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }, 10);
  }, []);

  // 四角形をダブルクリックしてテキスト編集開始
  const handleRectDoubleClick = useCallback((obj: EditorObject) => {
    setSelectedId(obj.id);
    setEditingTextId(obj.id);
    setEditingTextValue(obj.text || '');
    // 四角形内のテキストは4pxのパディングがあるため、位置とサイズを調整
    setEditingTextPosition({
      x: obj.x + 4,
      y: obj.y + 4,
      width: (obj.width || 100) - 8,
      height: (obj.height || 50) - 8,
    });
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const len = (obj.text || '').length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }, 10);
  }, []);

  // 描画開始
  const handleMouseDown = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    if (currentTool === 'marker' || currentTool === 'horizontal' || currentTool === 'rectangle' || currentTool === 'text') {
      const pos = stageRef.current?.getPointerPosition();
      if (pos) {
        setIsDrawing(true);
        setCurrentPoints([pos.x, pos.y]);
      }
    }
  }, [currentTool, stageRef]);

  // 描画中
  const handleMouseMove = useCallback(() => {
    if (!isDrawing) return;
    const pos = stageRef.current?.getPointerPosition();
    if (!pos) return;

    if (currentTool === 'marker') {
      setCurrentPoints(prev => [...prev, pos.x, pos.y]);
    } else if (currentTool === 'horizontal' || currentTool === 'rectangle' || currentTool === 'text') {
      setCurrentPoints(prev => [prev[0], prev[1], pos.x, pos.y]);
    }
  }, [isDrawing, currentTool, stageRef]);

  // 描画終了
  const handleMouseUp = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (currentPoints.length < 4) {
      setCurrentPoints([]);
      return;
    }

    if (currentTool === 'marker') {
      const newObj: EditorObject = {
        id: generateId(),
        type: 'marker',
        x: 0,
        y: 0,
        points: currentPoints,
        stroke: markerColor,
        strokeWidth: markerStrokeWidth,
      };
      setObjects(prev => [...prev, newObj]);
    } else if (currentTool === 'horizontal') {
      const newObj: EditorObject = {
        id: generateId(),
        type: 'line',
        x: 0,
        y: 0,
        points: [currentPoints[0], currentPoints[1], currentPoints[2], currentPoints[1]],
        stroke: '#000000',
        strokeWidth: 2,
      };
      setObjects(prev => [...prev, newObj]);
      setCurrentTool('select');
    } else if (currentTool === 'rectangle') {
      const x = Math.min(currentPoints[0], currentPoints[2]);
      const y = Math.min(currentPoints[1], currentPoints[3]);
      const width = Math.abs(currentPoints[2] - currentPoints[0]);
      const height = Math.abs(currentPoints[3] - currentPoints[1]);
      const newObj: EditorObject = {
        id: generateId(),
        type: 'rect',
        x,
        y,
        width,
        height,
        backgroundColor: rectFillEnabled ? rectFillColor : undefined,
        stroke: rectStrokeEnabled ? rectStrokeColor : undefined,
        strokeWidth: rectStrokeEnabled ? 2 : 0,
      };
      setObjects(prev => [...prev, newObj]);
      setCurrentTool('select');
    } else if (currentTool === 'text') {
      const x = Math.min(currentPoints[0], currentPoints[2]);
      const y = Math.min(currentPoints[1], currentPoints[3]);
      const width = Math.max(50, Math.abs(currentPoints[2] - currentPoints[0]));
      const height = Math.max(20, Math.abs(currentPoints[3] - currentPoints[1]));
      const newId = generateId();
      const newObj: EditorObject = {
        id: newId,
        type: 'text',
        x,
        y,
        text: '',
        fontSize: textFontSize,
        fontFamily: textFontFamily,
        fontStyle: textFontStyle,
        textAlign: textAlign,
        verticalAlign: verticalAlign,
        textColor: textColor,
        backgroundColor: textBgColor,
        width,
        height,
      };
      setObjects(prev => [...prev, newObj]);
      // 作成後すぐに編集モードに入る
      setSelectedId(newId);
      setEditingTextId(newId);
      setEditingTextValue('');
      setEditingTextPosition({ x, y, width, height });
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 10);
    }

    setCurrentPoints([]);
  }, [
    isDrawing, currentPoints, currentTool, setCurrentTool,
    markerColor, markerStrokeWidth,
    rectFillEnabled, rectFillColor, rectStrokeEnabled, rectStrokeColor,
    textFontSize, textFontFamily, textFontStyle, textAlign, verticalAlign, textColor, textBgColor,
  ]);

  // オブジェクト選択
  const handleObjectSelect = useCallback((id: string) => {
    if (currentTool === 'select') {
      setSelectedId(id);
    }
  }, [currentTool]);

  // オブジェクト移動
  const handleDragEnd = useCallback((id: string, e: Konva.KonvaEventObject<DragEvent>) => {
    setObjects(prev => prev.map(obj => {
      if (obj.id === id) {
        return { ...obj, x: e.target.x(), y: e.target.y() };
      }
      return obj;
    }));
  }, []);

  // 選択オブジェクト削除
  const handleDeleteSelected = useCallback(() => {
    if (selectedId) {
      setObjects(prev => prev.filter(obj => obj.id !== selectedId));
      setSelectedId(null);
    }
  }, [selectedId]);

  // 画像を追加
  const handleAddImage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string;
      if (!imageUrl) return;

      const img = new window.Image();
      img.onload = () => {
        // キャンバスサイズに収まるようにスケール
        const maxWidth = displaySize.width * 0.5;
        const maxHeight = displaySize.height * 0.5;
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        const newId = generateId();
        setLoadedImages(prev => ({ ...prev, [newId]: img }));

        const newObj: EditorObject = {
          id: newId,
          type: 'image',
          x: displaySize.offsetX + (displaySize.width - width) / 2,
          y: displaySize.offsetY + (displaySize.height - height) / 2,
          width,
          height,
          imageUrl,
        };
        setObjects(prev => [...prev, newObj]);
        // ツールを選択モードに切り替え、次のフレームで選択状態にする
        setCurrentTool('select');
        // 次のフレームまで待ってから選択（レンダリング完了後に選択）
        requestAnimationFrame(() => {
          setSelectedId(newId);
        });
      };
      img.src = imageUrl;
    };
    reader.readAsDataURL(file);

    event.target.value = '';
  }, [displaySize, setCurrentTool]);

  return {
    // State
    objects,
    setObjects,
    selectedId,
    setSelectedId,
    isDrawing,
    currentPoints,
    canvasSize,
    displaySize,
    cursorVisible,
    loadedImages,
    // テキスト編集
    editingTextId,
    setEditingTextId,
    editingTextValue,
    setEditingTextValue,
    editingTextPosition,
    setEditingTextPosition,
    textareaRef,
    // Handlers
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleStageClick,
    handleObjectSelect,
    handleDragEnd,
    handleDeleteSelected,
    handleTextDoubleClick,
    handleRectDoubleClick,
    handleTextEditConfirm,
    updateTextProperty,
    updateRectProperty,
    handleAddImage,
    refreshTransformer,
  };
};
